import React, { useRef, useState } from 'react';
import { TVirtualWindowData, TWindowPosition } from '../../types';
import Document from '../document-viewer/Document';
import useDraggable from '../../hooks/useDraggable';
import useResizable from '../../hooks/useResizable';
import './VirtualWindow.css';
import LatestDocsDocument from '../document-viewer/LatestDocsDocument';

interface VirtualWindowProps {
  window: TVirtualWindowData;
  onClose: (windowId: string) => void;
  onMinimize: (windowId: string) => void;
  onMaximize: (windowId: string) => void;
  onBringToFront: (windowId: string) => void;
  onUpdatePosition: (windowId: string, position: TWindowPosition) => void;
  onUpdateSize: (windowId: string, size: { width: number; height: number }) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const VirtualWindow: React.FC<VirtualWindowProps> = ({
  window,
  onClose,
  onMinimize,
  onMaximize,
  onBringToFront,
  onUpdatePosition,
  onUpdateSize,
  containerRef,

}) => {
  const windowRef = useRef<HTMLDivElement>(null);

  const { isDragging, handleMouseDown } = useDraggable({
    initialPosition: window.position,
    containerRef,
    elementRef: windowRef,
    onPositionChange: (position) => onUpdatePosition(window.id, position),
    disabled: window.isMaximized,
  });

  const { isResizing, handleResizeStart } = useResizable({
    elementRef: windowRef,
    containerRef,
    initialSize: window.size,
    onSizeChange: (size) => onUpdateSize(window.id, size),
    onPositionChange: (position) => onUpdatePosition(window.id, position),
    disabled: window.isMaximized,
  });

  const handleWindowClick = (): void => {
    onBringToFront(window.id);
  };

  const windowStyle: React.CSSProperties = window.isMaximized
    ? {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: window.zIndex,
    }
    : {
      position: 'absolute',
      top: `${window.position.y}px`,
      left: `${window.position.x}px`,
      width: `${window.size.width}px`,
      height: `${window.size.height}px`,
      zIndex: window.zIndex,
    };

  return (
    <div
      className={`virtual-window ${isDragging ? 'dragging' : ''} ${window.isMaximized ? 'maximized' : ''
        } ${window.focused ? 'focused' : ''} ${isResizing ? 'resizing' : ''}`}
      style={windowStyle}
      ref={windowRef}
      onMouseDown={handleWindowClick}
    >
      {
        !window.isMaximized
          ? <div className="window-header" onMouseDown={handleMouseDown}>
            <div className="window-title">
              <i className="file-icon">📄</i>
              <span>{window.title}</span>
            </div>
            <div className="window-controls">
              <button
                className="window-control minimize"
                onClick={() => onMinimize(window.id)}
              >
                &#8211;
              </button>
              <button
                className="window-control maximize"
                onClick={() => onMaximize(window.id)}
              >
                {window.isMaximized ? '❐' : '❒'}
              </button>
              <button
                className="window-control close"
                onClick={() => onClose(window.id)}
              >
                ✕
              </button>
            </div>
          </div>
          : null
      }
      <div className="window-content">
        {
          window.customWindow
            ? <LatestDocsDocument />
            : <Document
              docid={window.docId}
              anchorId={window.anchorId}
              windowId={window.id}
              docSearchText={window.innerSearch?.text}
              docSearchCaseSensitive={window.innerSearch?.caseSensitive}
              docSearchIndex={window.innerSearch?.index}
            />
        }
      </div>

      {/* Resize handles */}
      <div className="resize-handle n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
      <div className="resize-handle e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
      <div className="resize-handle s" onMouseDown={(e) => handleResizeStart(e, 's')} />
      <div className="resize-handle w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
      <div className="resize-handle nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
      <div className="resize-handle ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
      <div className="resize-handle se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
      <div className="resize-handle sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />
    </div>
  );
};
export default VirtualWindow;

